/**
 * `useConfirmedAction`: every destructive call goes through a confirmation
 * (safety-net SG6, apps/web/test/safety-net/sg6-confirmed-actions.test.ts;
 * the guard reads the call inside this hook's arguments). UI-HONEST builds
 * its first form for the session revokes; SN-11 adds the reason box (for
 * routes that take a `reason`) and the warnings exchange.
 *
 *   const revoke = useConfirmedAction({
 *     run: (session: Session) => post(`/security/sessions/${session.id}/revoke`, {}),
 *     invalidate: [["security", "sessions"]],
 *   });
 *   <Button onClick={() => revoke.ask(row, { title, body, confirmLabel })}>…
 *   {revoke.dialog}
 *
 * Asking opens the dialog; nothing is sent until the person confirms. One
 * request is in flight at a time. A refusal keeps the dialog open with the
 * API's error in the person's language, so they can try again or cancel; it
 * is never swallowed and never shown as success. The given queries are read
 * again after the call either way, and `onDone` runs after a success (for a
 * confirmation message).
 */
import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ConfirmDialog } from "@/components/ui";
import { ErrorText } from "@/components/ErrorText";
import { tenantKey } from "@/lib/api/hooks";

export interface ConfirmRequest {
  title: string;
  body: string;
  confirmLabel: string;
  tone?: "danger" | "primary";
}

export interface ConfirmedAction<T> {
  /** Open the confirmation for this target. */
  ask: (target: T, request: ConfirmRequest) => void;
  /** Render this where the dialog belongs (anywhere in the page). */
  dialog: ReactNode;
  /** Whether the confirmed call is running. */
  busy: boolean;
}

export function useConfirmedAction<T, R = unknown>({
  run,
  invalidate,
  onDone,
}: {
  run: (target: T) => Promise<R>;
  /** Query keys (tenant prefix added) to refetch after success. */
  invalidate?: string[][];
  onDone?: (result: R, target: T) => void;
}): ConfirmedAction<T> {
  const queryClient = useQueryClient();
  const [asked, setAsked] = useState<{
    target: T;
    request: ConfirmRequest;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const ask = (target: T, request: ConfirmRequest) => {
    setError(null);
    setAsked({ target, request });
  };

  const confirm = () => {
    if (!asked || busy) return;
    setBusy(true);
    setError(null);
    run(asked.target)
      .then((result) => {
        setAsked(null);
        onDone?.(result, asked.target);
      })
      .catch((err: unknown) => setError(err))
      .finally(() => {
        // Read again after a refusal too: a call that did part of its work
        // (the third of five revokes refused) must not leave the screen
        // showing the first two as still there.
        for (const key of invalidate ?? [])
          void queryClient.invalidateQueries({ queryKey: tenantKey(key) });
        setBusy(false);
      });
  };

  const dialog = asked ? (
    <ConfirmDialog
      title={asked.request.title}
      body={asked.request.body}
      confirmLabel={asked.request.confirmLabel}
      tone={asked.request.tone}
      busy={busy}
      error={error ? <ErrorText error={error} /> : undefined}
      onConfirm={confirm}
      onCancel={() => {
        if (!busy) setAsked(null);
      }}
    />
  ) : null;

  return { ask, dialog, busy };
}
